import { useEffect, useRef } from 'react'
import { useSocketContext } from '@/contexts/SocketProvider'
import { useHall } from '@/contexts/HallProvider'
import { useTurns } from '@/contexts/TurnsProvider'
import { useAxios } from '@/contexts/AxiosProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useParticipant } from '@videosdk.live/react-sdk'

export const useAssemblyLiveTranscription = () => {
  const { hall, updateTranscript, setLocalMetadata, hallMetadata } = useHall()
  const socket = useSocketContext()
  const { currentUser } = useHallUsers()
  const socketReadyRef = useRef(false)
  const { currentTurn } = useTurns()
  const { micOn } = useParticipant(currentUser?.videoSDKId)
  const wsRef = useRef<WebSocket | null>(null)
  const micOnRef = useRef(micOn)
  const axios = useAxios()
  const currentTranscriptIdRef = useRef<string | null>(null)
  const lastTurnOrderRef = useRef<number | null>(null)
  const localTranscriptCounterRef = useRef<number>(0)
  const bufferedTranscriptRef = useRef<string>('')
  const lastUserRef = useRef(currentUser)

  useEffect(() => {
    micOnRef.current = micOn
  }, [micOn])

  const startTranscription = async () => {
    // turnStarting is a local-only guard; do not publish/persist to server metadata.
    setLocalMetadata((prev: any) => ({ ...prev, turnStarting: true }))

    const { data } = await axios.get(`/transcription/assembly-token`)

    const url = `wss://streaming.assemblyai.com/v3/ws?sample_rate=16000&format_turns=true&token=${data.token}`
    const ws = new WebSocket(url)

    wsRef.current = ws

    ws.onopen = () => {
      setTimeout(() => {
        setLocalMetadata((prev: any) => ({ ...prev, turnStarting: false }))
      }, 3000)
    }

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data)

        if (msg?.type === 'Turn' && msg?.transcript && !hallMetadata?.turnStarting) {
        const currentTurn = hallMetadata?.turns[hallMetadata.activeTurn]
        const step = currentTurn.step
        const currentTurnOrder = typeof msg.turn_order === 'number' ? msg.turn_order : 0

        const isNewTurn =
          lastTurnOrderRef.current !== currentTurnOrder ||
          lastUserRef.current?.id !== currentUser?.id

        // Finalize and save the previous segment before starting a new one
        if (isNewTurn && bufferedTranscriptRef.current.trim()) {
          updateTranscript(step, {
            id: currentTranscriptIdRef.current!,
            text: bufferedTranscriptRef.current.trim(),
            hallUser: lastUserRef.current!,
            turnStep: currentTurn.step,
            role: lastUserRef.current.speakingRole,
          })
          bufferedTranscriptRef.current = ''
        }

        // If it's a new segment, assign a new ID
        if (isNewTurn) {
          lastTurnOrderRef.current = currentTurnOrder
          lastUserRef.current = currentUser
          localTranscriptCounterRef.current += 1
          currentTranscriptIdRef.current = `transcript-${Date.now()}-${
            localTranscriptCounterRef.current
          }`
        }

        // Store the latest text
        bufferedTranscriptRef.current = msg.transcript

        // Flush immediately if finalized or marked as end_of_turn
        if (msg.turn?.is_final || msg.turn?.end_of_turn) {
          updateTranscript(step, {
            id: currentTranscriptIdRef.current!,
            text: bufferedTranscriptRef.current.trim(),
            hallUser: currentUser,
            turnStep: currentTurn.step,
            role: currentUser.speakingRole,
          })
          bufferedTranscriptRef.current = ''
        }
      }
    }

    ws.onerror = () => {}
    ws.onclose = () => {
      setLocalMetadata((prev: any) => ({ ...prev, turnStarting: false }))
    }
  }

  const stopTranscription = () => {
    if (wsRef.current) {
      const step = hallMetadata?.turns[hallMetadata.activeTurn]?.step
      if (bufferedTranscriptRef.current.trim()) {
        updateTranscript(step, {
          id: currentTranscriptIdRef.current!,
          text: bufferedTranscriptRef.current.trim(),
          hallUser: lastUserRef.current!,
          turnStep: currentTurn.step,
          role: lastUserRef.current.speakingRole,
        })
        bufferedTranscriptRef.current = ''
      }

      wsRef.current.close()
      wsRef.current = null
      currentTranscriptIdRef.current = null
      lastTurnOrderRef.current = null
    }
  }

  const startUserTranscription = () => {
    if (socket) {
      socket.emit('start-user-transcription', {
        hallId: hall.id,
        users: [currentTurn.primarySpeakerId, currentTurn.secondarySpeakerId].filter(Boolean),
      })
      socketReadyRef.current = true
    }
  }

  const stopUserTranscription = () => {
    if (socket) {
      socket.emit('stop-user-transcription', {
        hallId: hall.id,
        users: [currentTurn.primarySpeakerId, currentTurn.secondarySpeakerId].filter(Boolean),
      })
      socketReadyRef.current = true
    }
  }

  const sendAudioChunk = (chunk: Int16Array) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN && micOnRef.current) {
      ws.send(chunk.buffer)
    }
  }

  return {
    startTranscription,
    sendAudioChunk,
    stopTranscription,
    startUserTranscription,
    stopUserTranscription,
  }
}
